import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getKvarxCities,
  getKvarxMarketplaceJobs,
  submitKvarxMarketplaceOffer,
  deleteKvarxMarketplaceOffer,
} from '../../../redux/api';
import { connectKvarxMarketSocket } from '../../../utils/kvarxRealtime';
import { alertOfferAccepted, isMyCompany, unlockKvarxAudio } from '../../../utils/kvarxAlerts';
import DatePickerField from '../../forms/job/DatePickerField';
import TimePickerField from '../../forms/job/TimePickerField';
import { getCompanyCityFromProfile } from '../../../utils/kvarxCity';

const selectClass =
  'h-8 min-w-[8.5rem] rounded border border-zinc-200 bg-zinc-50 px-2 text-xs text-zinc-800 outline-none focus:border-[#c8102e]';

const offerInputClass =
  'mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-[#c8102e]';

const offerControlClass =
  'h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm text-zinc-900 outline-none focus:border-[#c8102e]';

const getFilterStorageKey = () => {
  try {
    const profile = JSON.parse(localStorage.getItem('profile') || '{}');
    const userType = localStorage.getItem('userType') || 'company';
    const uid = profile?.result?._id || profile?.result?.id || 'anon';
    const companyId =
      profile?.result?.companyId || localStorage.getItem('currentCompanyId') || uid;
    return `kvarx_location_filters_${userType}_${companyId}_${uid}`;
  } catch {
    return 'kvarx_location_filters_default';
  }
};

const loadSavedFilters = (lockedCity = '') => {
  try {
    const raw = localStorage.getItem(getFilterStorageKey());
    if (!raw) return { city: lockedCity, municipality: '' };
    const parsed = JSON.parse(raw);
    const savedCity = String(parsed.city || '').trim();
    const municipality = String(parsed.municipality || '').trim();
    if (lockedCity && savedCity && savedCity !== lockedCity) {
      return { city: lockedCity, municipality: '' };
    }
    return {
      city: lockedCity || savedCity,
      municipality: lockedCity || savedCity ? municipality : '',
    };
  } catch {
    return { city: lockedCity, municipality: '' };
  }
};

const saveFilters = (filters) => {
  try {
    localStorage.setItem(getFilterStorageKey(), JSON.stringify(filters));
  } catch {
    // ignore quota / private mode
  }
};

const splitArrival = (value) => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
};

const emptyOfferForm = () => ({
  message: '',
  arrivalDate: '',
  arrivalTime: '',
  validUntilDate: '',
  validUntilTime: '',
  lateMinutes: '',
  declinedVisitFee: '',
  linePrices: {},
});

const truncate = (value, max = 56) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
};

const formatCreated = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('sr-RS', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const JobTrail = ({ job, fullAddress = false }) => {
  const street = [job.street, fullAddress ? job.streetNumber : ''].filter(Boolean).join(' ');
  const location = [
    job.city,
    job.municipality,
    street,
    fullAddress && job.floor ? `sprat ${job.floor}` : '',
    fullAddress && job.apartment ? `stan ${job.apartment}` : '',
  ]
    .filter(Boolean)
    .join(', ');
  const parts = [
    job.areaLabel,
    formatCreated(job.createdAt),
    job.jobCode ? `#${job.jobCode}` : '',
    job.itemLabel || job.intervention,
    truncate(job.description, 64),
    job.detailLabel,
    job.brand,
    job.machineModel,
    location,
  ].filter(Boolean);

  if (!parts.length) {
    return <span className="text-xs text-zinc-400">Nema podataka</span>;
  }

  return (
    <span className="block min-w-0 truncate text-xs text-zinc-600 sm:text-sm">
      {parts.map((part, index) => (
        <span key={`${part}-${index}`}>
          {index > 0 ? <span className="mx-1.5 text-zinc-300">·</span> : null}
          <span className={index === 0 ? 'font-medium text-zinc-800' : ''}>{part}</span>
        </span>
      ))}
    </span>
  );
};

const ChevronIcon = ({ open }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    aria-hidden
  >
    <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function KvarxLogoTitle() {
  return (
    <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">
      <span className="text-black">kvar</span>
      <span className="text-[#c8102e]">x</span>
    </h1>
  );
}

export default function KvarxMarketplaceView() {
  const [jobs, setJobs] = useState([]);
  const [cityCatalog, setCityCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedJobId, setExpandedJobId] = useState('');
  const [activeJobId, setActiveJobId] = useState('');
  const [offerForm, setOfferForm] = useState(emptyOfferForm);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [lockedCity, setLockedCity] = useState(() => getCompanyCityFromProfile());
  const [available, setAvailable] = useState(() => Boolean(getCompanyCityFromProfile()));
  const [filters, setFilters] = useState(() => loadSavedFilters(getCompanyCityFromProfile()));

  const loadJobs = useCallback(async (opts = {}) => {
    if (!opts.silent) setLoading(true);
    setError('');
    try {
      const response = await getKvarxMarketplaceJobs();
      const nextCity = String(response.data.city || getCompanyCityFromProfile() || '').trim();
      const isAvailable = response.data.available !== false && Boolean(nextCity);
      setAvailable(isAvailable);
      if (nextCity) {
        setLockedCity(nextCity);
        setFilters((prev) => {
          if (prev.city === nextCity) return prev;
          return { city: nextCity, municipality: '' };
        });
      }
      setJobs(response.data.jobs || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Soba poslova nije učitana.');
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    let cancelled = false;
    getKvarxCities()
      .then((response) => {
        if (!cancelled) setCityCatalog(response.data.cities || []);
      })
      .catch(() => {
        if (!cancelled) setCityCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const unlock = () => unlockKvarxAudio();
    window.addEventListener('pointerdown', unlock);
    unlock();
    const socket = connectKvarxMarketSocket();
    const refresh = () => loadJobs({ silent: true });
    const onDiagnosis = (payload) => {
      setJobs((prev) =>
        prev.map((job) =>
          String(job.id) === String(payload.jobId) ? { ...job, diagnosis: payload.diagnosis } : job
        )
      );
    };
    const onOffer = (payload) => {
      if (payload?.kind === 'accepted' || payload?.jobStatus === 'agreed' || payload?.jobStatus === 'closed') {
        alertOfferAccepted(isMyCompany(payload?.companyId), payload?.jobId);
      }
      refresh();
    };
    socket.on('kvarx_job', refresh);
    socket.on('kvarx_offer', onOffer);
    socket.on('kvarx_diagnosis', onDiagnosis);
    return () => {
      window.removeEventListener('pointerdown', unlock);
      socket.off('kvarx_job', refresh);
      socket.off('kvarx_offer', onOffer);
      socket.off('kvarx_diagnosis', onDiagnosis);
    };
  }, [loadJobs]);

  const selectedCity = useMemo(
    () =>
      cityCatalog.find(
        (city) =>
          city.name === filters.city ||
          city.name === lockedCity ||
          String(city.name).toLowerCase() === String(filters.city || lockedCity).toLowerCase()
      ) || null,
    [cityCatalog, filters.city, lockedCity]
  );

  const municipalityOptions = useMemo(() => {
    const list = selectedCity?.municipalities || [];
    return [...list].sort((a, b) => a.localeCompare(b, 'sr'));
  }, [selectedCity]);

  // Ako sačuvana opština ne pripada gradu firme — očisti je
  useEffect(() => {
    if (!filters.municipality) return;
    if (!filters.city) {
      setFilters((prev) => {
        const next = { ...prev, municipality: '' };
        saveFilters(next);
        return next;
      });
      return;
    }
    if (!cityCatalog.length) return;
    if (municipalityOptions.includes(filters.municipality)) return;
    setFilters((prev) => {
      const next = { ...prev, municipality: '' };
      saveFilters(next);
      return next;
    });
  }, [filters.city, filters.municipality, municipalityOptions, cityCatalog.length]);

  useEffect(() => {
    saveFilters(filters);
  }, [filters]);

  const filteredJobs = useMemo(() => {
    const cityKey = String(filters.city || lockedCity || '').trim().toLowerCase();
    return jobs.filter((job) => {
      if (cityKey && String(job.city || '').trim().toLowerCase() !== cityKey) return false;
      if (filters.municipality && job.municipality !== filters.municipality) return false;
      return true;
    });
  }, [jobs, filters, lockedCity]);

  useEffect(() => {
    if (typeof window === 'undefined' || loading) return undefined;
    window.dispatchEvent(
      new CustomEvent('kvarx-nav-counts', {
        detail: {
          matched: filteredJobs.length,
          total: jobs.length,
        },
      })
    );
    return undefined;
  }, [filteredJobs.length, jobs.length, loading]);

  const updateMunicipality = (municipality) => {
    setFilters((prev) => ({ ...prev, city: lockedCity || prev.city, municipality }));
  };

  const openOffer = (job) => {
    setExpandedJobId(job.id);
    setActiveJobId(job.id);
    setNotice('');
    const linePrices = {};
    (job.myOffer?.lineItems || []).forEach((item) => {
      if (item.amount != null) linePrices[item.key] = String(item.amount);
    });
    const arrival = splitArrival(job.myOffer?.earliestArrival);
    const validUntil = splitArrival(job.myOffer?.validUntil);
    setOfferForm({
      message: job.myOffer?.message || '',
      arrivalDate: arrival.date,
      arrivalTime: arrival.time,
      validUntilDate: validUntil.date,
      validUntilTime: validUntil.time,
      lateMinutes: job.myOffer?.lateMinutes != null ? String(job.myOffer.lateMinutes) : '',
      declinedVisitFee:
        job.myOffer?.declinedVisitFee != null ? String(job.myOffer.declinedVisitFee) : '',
      linePrices,
    });
  };

  const toggleExpand = (job) => {
    const next = expandedJobId === job.id ? '' : job.id;
    setExpandedJobId(next);
    setNotice('');
    if (!next) {
      setActiveJobId('');
      return;
    }
    openOffer(job);
  };

  const submitOffer = async (event) => {
    event.preventDefault();
    if (!activeJobId) return;
    const job = jobs.find((row) => row.id === activeJobId);
    const fixes = job?.diagnosis?.possibleFixes || [];
    setSaving(true);
    setNotice('');
    try {
      await submitKvarxMarketplaceOffer(activeJobId, {
        message: offerForm.message,
        earliestArrival:
          offerForm.arrivalDate && offerForm.arrivalTime
            ? `${offerForm.arrivalDate}T${offerForm.arrivalTime}`
            : offerForm.arrivalDate || null,
        lateMinutes: offerForm.lateMinutes,
        validUntil:
          offerForm.validUntilDate && offerForm.validUntilTime
            ? `${offerForm.validUntilDate}T${offerForm.validUntilTime}`
            : offerForm.validUntilDate || null,
        declinedVisitFee: offerForm.declinedVisitFee,
        lineItems: fixes.map((fix) => ({
          key: fix.key,
          label: fix.label,
          amount:
            offerForm.linePrices[fix.key] === '' || offerForm.linePrices[fix.key] == null
              ? null
              : Number(offerForm.linePrices[fix.key]),
        })),
      });
      setNotice('Ponuda je poslata klijentu na kvarx.');
      setActiveJobId('');
      await loadJobs({ silent: true });
    } catch (err) {
      setNotice(err.response?.data?.message || 'Ponuda nije poslata.');
    } finally {
      setSaving(false);
    }
  };

  const withdrawOffer = async (job) => {
    if (!job?.id || job.myOffer?.status === 'accepted') return;
    if (!window.confirm('Obrisati ovu ponudu?')) return;
    setSaving(true);
    setNotice('');
    try {
      await deleteKvarxMarketplaceOffer(job.id);
      setNotice('Ponuda je obrisana.');
      setExpandedJobId('');
      setActiveJobId('');
      await loadJobs({ silent: true });
    } catch (err) {
      setNotice(err.response?.data?.message || 'Ponuda nije obrisana.');
    } finally {
      setSaving(false);
    }
  };

  const hasMunicipalityFilter = Boolean(filters.municipality);

  return (
    <div
      className="min-h-full bg-white text-zinc-900"
      style={{
        backgroundColor: '#ffffff',
        color: '#18181b',
        minHeight: '100%',
      }}
    >
      <style>{`
        .kvarx-datepicker {
          border: 1px solid #e4e4e7;
          font-family: inherit;
        }
        .kvarx-datepicker .react-datepicker__header {
          background: #111111;
          border-bottom: 1px solid #111111;
        }
        .kvarx-datepicker .react-datepicker__current-month,
        .kvarx-datepicker .react-datepicker__day-name,
        .kvarx-datepicker .react-datepicker__day--outside-month {
          color: #f4f4f5;
        }
        .kvarx-datepicker .react-datepicker__day-name { color: #d4d4d8; }
        .kvarx-datepicker .react-datepicker__navigation-icon::before {
          border-color: #ffffff;
        }
        .kvarx-datepicker .react-datepicker__day--selected,
        .kvarx-datepicker .react-datepicker__day--keyboard-selected {
          background: #c8102e !important;
          color: #ffffff !important;
        }
        .kvarx-datepicker .react-datepicker__day:hover {
          background: rgba(200, 16, 46, 0.12);
        }
        .kvarx-datepicker .react-datepicker__day--today {
          font-weight: 700;
          color: #c8102e;
        }
      `}</style>
      <div className="w-full border-b border-zinc-200 bg-white">
        <div className="flex w-full flex-col items-center justify-center px-4 py-6 text-center">
          <KvarxLogoTitle />
          <p className="mt-1.5 text-xs text-zinc-500">
            {lockedCity ? `Poslovi iz grada firme: ${lockedCity}` : 'Samo poslovi sa platforme'}
          </p>
        </div>
      </div>

      <div className="bg-zinc-50/80 p-4 md:p-5" style={{ backgroundColor: '#fafafa' }}>
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-zinc-200 bg-white px-2.5 py-2 shadow-sm">
          <span className="rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-700">
            Ukupno <b>{loading ? '—' : jobs.length}</b>
          </span>
          <span className="rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-700">
            Prikaz <b>{loading ? '—' : filteredJobs.length}</b>
          </span>
          <span className="rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-700">
            Moje ponude <b>{loading ? '—' : jobs.filter((job) => job.myOffer).length}</b>
          </span>

          <span className="mx-1 hidden h-5 w-px bg-zinc-200 sm:block" />

          <span
            className="h-8 inline-flex items-center rounded border border-zinc-200 bg-zinc-50 px-2 text-xs font-medium text-zinc-800"
            title="Grad se uzima sa registracije firme i ne može da se menja"
          >
            {lockedCity || 'Grad nije podešen'}
          </span>

          <select
            className={selectClass}
            value={filters.municipality}
            onChange={(event) => updateMunicipality(event.target.value)}
            disabled={!lockedCity || !municipalityOptions.length}
            title="Opštine"
          >
            <option value="">Opštine</option>
            {municipalityOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          {hasMunicipalityFilter ? (
            <button
              type="button"
              onClick={() =>
                setFilters({ city: lockedCity || filters.city, municipality: '' })
              }
              className="h-8 rounded border border-zinc-200 bg-white px-2 text-xs text-zinc-600 hover:bg-zinc-50"
            >
              Reset
            </button>
          ) : null}
        </div>

        {loading ? <p className="text-sm text-zinc-600">Učitavam...</p> : null}
        {error ? <p className="text-sm text-[#c8102e]">{error}</p> : null}
        {notice ? <p className="mb-3 text-sm text-emerald-700">{notice}</p> : null}

        {!loading && !error && !available ? (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-6 text-center text-sm text-zinc-600">
            kvarx je dostupan kada je na firmi podešen grad pri registraciji.
          </div>
        ) : null}

        {!loading && !error && available && filteredJobs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-6 text-center text-sm text-zinc-600">
            {filters.municipality
              ? `Nema poslova za ${filters.municipality}.`
              : `Nema poslova u gradu ${lockedCity || 'firme'}.`}
          </div>
        ) : null}

        <div className="space-y-2">
          {filteredJobs.map((job) => {
            const isOpen = expandedJobId === job.id;
            return (
              <div
                key={job.id}
                className={`overflow-hidden rounded-xl border bg-white shadow-sm transition ${
                  isOpen
                    ? 'border-[#c8102e]/35 ring-1 ring-[#c8102e]/15'
                    : 'border-zinc-200 hover:border-zinc-300'
                }`}
              >
                <button
                  type="button"
                  onClick={() => toggleExpand(job)}
                  className="flex w-full items-center gap-3 px-3.5 py-3 text-left sm:gap-4 sm:px-4"
                  aria-expanded={isOpen}
                >
                  <div className="min-w-0 flex-1">
                    <JobTrail job={job} />
                  </div>

                  <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                    <span className="rounded-md bg-zinc-100 px-2 py-1 text-[11px] font-medium text-zinc-600">
                      {job.myOffer ? 'vaša ponuda' : job.status === 'offered' ? 'ima ponuda' : 'otvoren'}
                    </span>
                    <ChevronIcon open={isOpen} />
                  </div>
                </button>

                {isOpen ? (
                  <div className="border-t border-zinc-100 bg-zinc-50/60 px-3.5 py-3.5 sm:px-4 sm:py-4">
                    {job.images?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {job.images.map((src) => (
                          <a
                            key={src}
                            href={src}
                            target="_blank"
                            rel="noreferrer"
                            className="block h-16 w-16 overflow-hidden ring-1 ring-zinc-200"
                          >
                            <img src={src} alt="" className="h-full w-full object-cover" />
                          </a>
                        ))}
                      </div>
                    ) : null}
                    <p className="mt-2 text-[11px] text-zinc-400">
                      Tačan broj, sprat i stan se otkrivaju tek nakon prihvatanja ponude.
                    </p>

                    {job.diagnosis?.status === 'pending' ? (
                      <p className="mt-3 text-xs text-zinc-500">AI procenjuje mogući kvar...</p>
                    ) : null}

                    {job.myOffer?.status === 'accepted' ? (
                      <p className="mt-3 text-sm font-medium text-emerald-700">
                        Ponuda je prihvaćena. Posao je u Ugovorenim.
                      </p>
                    ) : (
                    <form onSubmit={submitOffer} className="mt-3 space-y-2">
                      {(job.diagnosis?.possibleFixes || []).length ? (
                        <ul className="space-y-2">
                          <li className="text-xs text-zinc-700">
                            Cena dolaska i konstatacije je uračunato u cenu, kao i rezervni deo.
                          </li>
                          {job.diagnosis.possibleFixes.map((fix) => {
                            const isOstalo =
                              String(fix.key || '').toLowerCase() === 'ostalo' ||
                              String(fix.label || '').trim().toLowerCase() === 'ostalo';
                            return (
                            <li
                              key={fix.key}
                              className="flex flex-wrap items-center gap-2 text-xs text-zinc-700"
                            >
                              <span className="min-w-0 flex-1">
                                {fix.label}
                                {fix.probability != null ? (
                                  <span className="text-zinc-400"> · {fix.probability}%</span>
                                ) : null}
                              </span>
                              {isOstalo ? null : (
                                <>
                                  <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    placeholder="0"
                                    className="h-8 w-24 border border-zinc-200 bg-white px-2 text-sm text-zinc-900 outline-none focus:border-[#c8102e]"
                                    value={
                                      expandedJobId === job.id ? offerForm.linePrices[fix.key] || '' : ''
                                    }
                                    onChange={(event) =>
                                      setOfferForm((prev) => ({
                                        ...prev,
                                        linePrices: {
                                          ...prev.linePrices,
                                          [fix.key]: event.target.value,
                                        },
                                      }))
                                    }
                                  />
                                  <span className="shrink-0 text-xs text-zinc-500">RSD</span>
                                </>
                              )}
                            </li>
                            );
                          })}
                          <li className="flex flex-wrap items-center gap-2 pt-1 text-xs text-zinc-600">
                            <span className="min-w-0 flex-1">
                              * Ukoliko stranka odustane od popravke cena dolaska i konstatacije je
                            </span>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              className="h-8 w-24 border border-zinc-200 bg-white px-2 text-sm text-zinc-900 outline-none focus:border-[#c8102e]"
                              value={
                                expandedJobId === job.id ? offerForm.declinedVisitFee || '' : ''
                              }
                              onChange={(event) =>
                                setOfferForm((prev) => ({
                                  ...prev,
                                  declinedVisitFee: event.target.value,
                                }))
                              }
                            />
                            <span className="shrink-0 text-xs text-zinc-500">RSD</span>
                          </li>
                        </ul>
                      ) : job.diagnosis?.status !== 'pending' ? (
                        <p className="text-xs text-zinc-500">
                          Sačekajte AI procenu pa unesite cene pored stavki.
                        </p>
                      ) : null}
                      <div className="grid grid-cols-1 items-end gap-2 lg:grid-cols-4">
                        <label className="block text-xs text-zinc-700">
                          Datum dolaska
                          <div className="mt-1">
                            <DatePickerField
                              value={expandedJobId === job.id ? offerForm.arrivalDate : ''}
                              onChange={(arrivalDate) =>
                                setOfferForm((prev) => ({ ...prev, arrivalDate }))
                              }
                              placeholder="dd.mm.yyyy"
                              minDate={new Date()}
                              inputClass={offerControlClass}
                              calendarClassName="kvarx-datepicker"
                            />
                          </div>
                        </label>
                        <label className="block text-xs text-zinc-700">
                          Satnica dolaska
                          <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_5.5rem] sm:items-end sm:gap-2">
                            <div className="min-w-0">
                              <TimePickerField
                                value={expandedJobId === job.id ? offerForm.arrivalTime : ''}
                                onChange={(arrivalTime) =>
                                  setOfferForm((prev) => ({ ...prev, arrivalTime }))
                                }
                                inputClass={offerControlClass}
                                placeholder="HH:MM"
                              />
                            </div>
                            <div className="min-w-0">
                              <span className="mb-1 block text-[11px] text-zinc-500 sm:sr-only">
                                Okvirno ± kasnjenje (min)
                              </span>
                              <input
                                type="number"
                                min="0"
                                max="180"
                                step="5"
                                title="Okvirno ± kasnjenje u minutima"
                                placeholder="± min"
                                className={`${offerControlClass} w-full px-2`}
                                value={expandedJobId === job.id ? offerForm.lateMinutes : ''}
                                onChange={(event) =>
                                  setOfferForm((prev) => ({
                                    ...prev,
                                    lateMinutes: event.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </label>
                        <label className="block text-xs text-zinc-700">
                          Ponuda važi do <span className="font-normal text-zinc-400">(opciono)</span>
                          <div className="mt-1">
                            <DatePickerField
                              value={expandedJobId === job.id ? offerForm.validUntilDate : ''}
                              onChange={(validUntilDate) =>
                                setOfferForm((prev) => ({ ...prev, validUntilDate }))
                              }
                              placeholder="dd.mm.yyyy"
                              minDate={new Date()}
                              inputClass={offerControlClass}
                              calendarClassName="kvarx-datepicker"
                            />
                          </div>
                        </label>
                        <label className="block text-xs text-zinc-700">
                          Satnica
                          <div className="mt-1">
                            <TimePickerField
                              value={expandedJobId === job.id ? offerForm.validUntilTime : ''}
                              onChange={(validUntilTime) =>
                                setOfferForm((prev) => ({ ...prev, validUntilTime }))
                              }
                              inputClass={offerControlClass}
                              placeholder="HH:MM"
                            />
                          </div>
                        </label>
                      </div>
                      <label className="block text-xs text-zinc-700">
                        Poruka
                        <textarea
                          rows={2}
                          className={offerInputClass}
                          value={expandedJobId === job.id ? offerForm.message : ''}
                          onChange={(event) =>
                            setOfferForm((prev) => ({ ...prev, message: event.target.value }))
                          }
                          placeholder="Kratko šta uključuje ponuda"
                        />
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="submit"
                          disabled={saving || !(job.diagnosis?.possibleFixes || []).length}
                          className="bg-[#c8102e] px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                        >
                          {saving ? 'Šaljem...' : job.myOffer ? 'Izmeni ponudu' : 'Pošalji ponudu'}
                        </button>
                        {job.myOffer ? (
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => withdrawOffer(job)}
                            className="px-3 py-1.5 text-xs font-semibold text-zinc-600 hover:text-[#c8102e] disabled:opacity-60"
                          >
                            Obriši ponudu
                          </button>
                        ) : null}
                      </div>
                    </form>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
